import { type CSSProperties } from "react";

/** ---------- Types ---------- */
export type Slice = { label: string; value: number; color: string };

/** ---------- Helpers ---------- */
function buildConicGradient(data: Slice[]): string {
  const total = Math.max(1, data.reduce((s, d) => s + d.value, 0));
  let current = 0;
  const stops = data.map((d) => {
    const start = (current / total) * 360;
    const end = ((current + d.value) / total) * 360;
    current += d.value;
    return `${d.color} ${start}deg ${end}deg`;
  });
  return `conic-gradient(${stops.join(", ")})`;
}

function format(n: number) {
  return Intl.NumberFormat().format(n);
}

/** Compact donut + legend, no card chrome. Used when a parent card already
 * provides the border / title / total (e.g. the dashboard ChartCard). */
function DonutBody({ data, total }: { data: Slice[]; total: number }) {
  const style: CSSProperties = { background: buildConicGradient(data) };
  const grandTotal = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="grid grid-cols-1 items-center gap-5 sm:grid-cols-[auto_1fr]">
      {/* Donut */}
      <div className="relative mx-auto h-36 w-36 sm:h-40 sm:w-40">
        <div className="h-full w-full rounded-full" style={style} aria-hidden />
        <div className="absolute inset-5 grid place-items-center rounded-full bg-white">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">Total</div>
            <div className="text-lg font-semibold tabular-nums text-zinc-900">
              {format(total)}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <ul className="space-y-1.5">
        {data.map((d) => {
          const pct = grandTotal > 0 ? Math.round((d.value / grandTotal) * 100) : 0;
          return (
            <li key={d.label} className="flex items-center justify-between gap-3 text-sm">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: d.color }}
                />
                <span className="truncate text-zinc-600" title={d.label}>
                  {d.label}
                </span>
              </div>
              <div className="flex shrink-0 items-baseline gap-1.5 tabular-nums">
                <span className="font-medium text-zinc-900">{format(d.value)}</span>
                <span className="text-xs text-zinc-400">{pct}%</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** ---------- Donut Chart (pure UI) ---------- */
export default function DonutChart({
  title,
  data,
  subtitle,
  embedded = false,
}: {
  title: string;
  data: Slice[];
  subtitle?: string;
  /** When true, render only the donut + legend (no outer card / header). */
  embedded?: boolean;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);

  if (embedded) {
    return <DonutBody data={data} total={total} />;
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
      <div className="mb-4 flex items-end justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {subtitle ? <p className="text-xs text-gray-500">{subtitle}</p> : null}
        </div>
        <div className="text-xs text-gray-500">
          Total: <span className="font-medium text-gray-700">{format(total)}</span>
        </div>
      </div>

      <DonutBody data={data} total={total} />
    </div>
  );
}
